/**
 * WorkspaceAccessCard — a platform admin opens AdOpt to an org workspace's
 * members (or closes it again).
 *
 * AdOpt is otherwise platform-admin-only. Opened, the workspace's members use
 * it with their workspace role: viewers read, members manage campaigns
 * (including Resume, within the spend limits), owners / admins also hold the
 * spend limits, trial settings, the Google connection and automatic mode. The
 * note is required and audited with the change.
 *
 * Rendered only for platform admins on an org workspace (the server route is
 * requireAdmin, and refuses a personal workspace).
 */

import { useState } from 'react';
import {
  useAdoptWorkspaceAccess,
  useGrantAdoptWorkspaceAccess,
  useRevokeAdoptWorkspaceAccess,
} from '../hooks/useAdvertisingOptimization';

const NOTE_MAX_CHARS = 500;

export function WorkspaceAccessCard({ holderId }: { holderId: string }) {
  const accessQuery = useAdoptWorkspaceAccess(holderId, true);
  const grant = useGrantAdoptWorkspaceAccess(holderId);
  const revoke = useRevokeAdoptWorkspaceAccess(holderId);
  const [note, setNote] = useState('');

  if (accessQuery.isLoading) {
    return <section className="text-sm text-muted-foreground">Loading workspace access…</section>;
  }
  const access = accessQuery.data ?? null;

  return (
    <section
      aria-labelledby="adopt-workspace-access-title"
      className="rounded-lg border border-border p-4 flex flex-col gap-3"
    >
      <div>
        <h2 id="adopt-workspace-access-title" className="font-medium">
          Open AdOpt to this workspace
        </h2>
        <p className="text-sm text-muted-foreground">
          Platform admins only. Opened, this workspace&apos;s members can use AdOpt here with their
          workspace role: members manage campaigns and can start ads within the spend limits; only
          owners and admins can change the limits, trial settings, the Google connection and
          automatic mode.
        </p>
      </div>

      {access ? (
        <div className="flex flex-col gap-2 text-sm">
          <span className="text-emerald-700">
            Open since {new Date(access.enabledAt).toLocaleString()}.
          </span>
          <span className="text-muted-foreground">Note: {access.note}</span>
          <div>
            <button
              onClick={() => revoke.mutate()}
              disabled={revoke.isPending}
              className="px-3 py-1.5 rounded-md border border-rose-300 text-rose-700 text-sm disabled:opacity-50"
            >
              {revoke.isPending ? 'Closing…' : 'Close to members'}
            </button>
          </div>
          <span className="text-xs text-muted-foreground">
            Closing stops members using AdOpt here. It does not pause anything already running —
            pause campaigns first if they must stop.
          </span>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium" htmlFor="adopt-workspace-access-note">
            Why (recorded in the audit log)
          </label>
          <textarea
            id="adopt-workspace-access-note"
            className="rounded-md border border-border bg-background px-3 py-2 text-sm"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={NOTE_MAX_CHARS}
            rows={2}
            placeholder="e.g. Marketing team runs the build-your-own-app campaign"
          />
          <div>
            <button
              onClick={() => grant.mutate(note.trim(), { onSuccess: () => setNote('') })}
              disabled={!note.trim() || grant.isPending}
              className="px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-sm disabled:opacity-50"
            >
              {grant.isPending ? 'Opening…' : 'Open to members'}
            </button>
          </div>
          {grant.isError && (
            <span className="text-sm text-rose-600">
              Could not open it — only a live org workspace can be opened.
            </span>
          )}
        </div>
      )}
    </section>
  );
}
